export type StatusColorHue = "neutral" | "blue" | "green" | "red" | "orange";
export type StatusColorTier = "high" | "medium" | "low";

export interface StatusColor {
  hue?: StatusColorHue;
  bgColor: string;
  textColor: string;
  hoverBgColor?: string;
  hoverTextColor?: string;
}

export interface TierColors {
  bgColor: string;
  textColor: string;
  hoverBgColor: string;
  hoverTextColor: string;
}

// Display order for the tabbed color-picker popup's swatch list. Consumers needing a specific
// hue (e.g. a default color) should use statusColorByHue/defaultStatusColor below, not an index.
export const statusColorPalette: StatusColor[] = [
  { hue: "neutral", bgColor: "#DEE3E7", textColor: "#272E35", hoverBgColor: "#555F6D", hoverTextColor: "#FFFFFF" },
  { hue: "blue", bgColor: "#D7E4FF", textColor: "#113997", hoverBgColor: "#3062D4", hoverTextColor: "#FFFFFF" },
  { hue: "green", bgColor: "#C6F1DA", textColor: "#0E4E30", hoverBgColor: "#1D7C4D", hoverTextColor: "#FFFFFF" },
  { hue: "red", bgColor: "#FCD9D9", textColor: "#6F2020", hoverBgColor: "#C53434", hoverTextColor: "#FFFFFF" },
  { hue: "orange", bgColor: "#FCDEC0", textColor: "#7A4510", hoverBgColor: "#F59638", hoverTextColor: "#FFFFFF" },
];

export const statusColorByHue: Record<StatusColorHue, StatusColor> = Object.fromEntries(
  statusColorPalette.map((color) => [color.hue, color]),
) as Record<StatusColorHue, StatusColor>;

export const defaultStatusColor: StatusColor = statusColorByHue.neutral;

export const defaultPriorityColor: StatusColor = statusColorByHue.neutral;

// High = the hue's own hover pair (no separate "higher" state, so it doubles as its own hover).
// Medium = the base pair; its hover pair is the existing "high" data, unchanged from before tiers existed.
// Low = transparent background, same text color as Medium (confirmed identical across every hue in Figma).
export function getTierColors(color: StatusColor, tier: StatusColorTier): TierColors {
  if (tier === "high") {
    const bgColor = color.hoverBgColor ?? color.bgColor;
    const textColor = color.hoverTextColor ?? color.textColor;
    return { bgColor, textColor, hoverBgColor: bgColor, hoverTextColor: textColor };
  }
  if (tier === "low") {
    return {
      bgColor: "transparent",
      textColor: color.textColor,
      hoverBgColor: "transparent",
      hoverTextColor: color.textColor,
    };
  }
  return {
    bgColor: color.bgColor,
    textColor: color.textColor,
    hoverBgColor: color.hoverBgColor ?? color.bgColor,
    hoverTextColor: color.hoverTextColor ?? color.textColor,
  };
}
